import { useState } from "react";
import toast from "react-hot-toast";

import { useExternalServiceAssignee, useOnfleet } from "@web-app/api";
import {
  AccordionV2,
  Button,
  CopyableField,
  Icon,
  LoadingIcon,
  RefreshButton,
} from "@web-app/components";
import { OnfleetTask, OnfleetTaskState } from "@web-app/types";
import http from "@web-app/utils/http";

const getTaskStateBadge = (state: OnfleetTaskState) => {
  switch (state) {
    case OnfleetTaskState.Unassigned:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
          Unassigned
        </span>
      );
    case OnfleetTaskState.Assigned:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
          Assigned
        </span>
      );
    case OnfleetTaskState.Active:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
          Active
        </span>
      );
    case OnfleetTaskState.Completed:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
          Completed
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
          Unknown
        </span>
      );
  }
};

const OnfleetTaskDetails = ({ task }: { task: OnfleetTask }) => {
  return (
    <div>
      <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Status</span>
          <span className="tw-details-field-value">
            {getTaskStateBadge(task.state)}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Type</span>
          <span className="tw-details-field-value">
            {task.pickupTask ? "Pickup" : "Drop-off"}
          </span>
        </div>
        {task.shortId && (
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Short ID</span>
            <span className="tw-details-field-value">{task.shortId}</span>
          </div>
        )}
      </div>
      {task.destination?.address && (
        <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Address</span>
            <span className="tw-details-field-value">
              {[
                task.destination.address.number,
                task.destination.address.street,
                task.destination.address.city,
                task.destination.address.state,
                task.destination.address.postalCode,
              ]
                .filter(Boolean)
                .join(", ")}
            </span>
          </div>
        </div>
      )}
      {task.recipients?.length > 0 && (
        <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Recipient</span>
            <span className="tw-details-field-value">
              {task.recipients[0].name}
            </span>
          </div>
          {task.recipients[0].phone && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Phone</span>
              <span className="tw-details-field-value">
                {task.recipients[0].phone}
              </span>
            </div>
          )}
        </div>
      )}
      {task.trackingURL && (
        <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title flex items-center">
              Tracking Link
            </span>
            <span
              className="tw-details-field-value"
              style={{ overflowWrap: "anywhere" }}
            >
              <CopyableField
                value={task.trackingURL}
                appearance="link-inline"
                className="text-xs font-normal"
              />
            </span>
          </div>
        </div>
      )}
      {task.completionDetails && (
        <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
          {task.completionDetails.notes && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Notes</span>
              <span className="tw-details-field-value">
                {task.completionDetails.notes}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function OnfleetDeliveryInfo({
  entity,
  entityId,
  onCancelSuccess,
}: {
  entity: string;
  entityId: string;
  onCancelSuccess?: () => void;
}) {
  const {
    data: tasks,
    isLoading,
    refetch,
    isRefetching,
  } = useOnfleet(entity, entityId);

  const { data: esa } = useExternalServiceAssignee(entity, entityId);

  const getSerializedTasks = () => {
    if (!tasks) return [];
    const serializedTasks: OnfleetTask[] = [];
    if (esa?.type === "onfleet") {
      esa.deliveryReferences.forEach((ref) => {
        const task = tasks.find((t) => t.id === ref);
        if (task) {
          serializedTasks.push(task);
        }
      });
    }
    return serializedTasks;
  };

  const [isCancelling, setIsCancelling] = useState(false);

  if (isLoading || isRefetching)
    return <LoadingIcon className="w-8 h-8 m-auto mt-10" />;

  if (!tasks || tasks.length === 0)
    return <div>Could not fetch Onfleet tasks</div>;

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      const resp = await http.delete(
        `/integrations/onfleet/${entity}/${entityId}/tasks`,
      );
      if (resp.status === 200) {
        toast.success("Onfleet tasks canceled successfully");
        onCancelSuccess?.();
      }
    } catch {
      toast.error("Failed to cancel Onfleet tasks");
    } finally {
      setIsCancelling(false);
    }
  };

  const canCancel = () => {
    return tasks.every(
      (task) =>
        task.state === OnfleetTaskState.Unassigned ||
        task.state === OnfleetTaskState.Assigned,
    );
  };

  if (tasks.length === 1) {
    return (
      <div className="flex flex-col">
        <div className="flex justify-end mr-5 gap-2">
          <RefreshButton onClick={() => refetch()} />
          <Button
            size="sm"
            onClick={handleCancel}
            color="red"
            disabled={!canCancel()}
            isLoading={isCancelling}
          >
            {isCancelling ? "Cancelling..." : "Cancel Task"}
          </Button>
        </div>
        <OnfleetTaskDetails task={tasks[0]} />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex justify-end mr-5 gap-2">
        <RefreshButton onClick={() => refetch()} />
        <Button
          size="sm"
          onClick={handleCancel}
          color="red"
          disabled={!canCancel()}
          isLoading={isCancelling}
        >
          {isCancelling ? "Cancelling..." : "Cancel Tasks"}
        </Button>
      </div>
      {getSerializedTasks().map((task, index) => (
        <AccordionV2 key={index} defaultOpen={index === 0} isBorderless>
          <AccordionV2.Label textAlign="left" labelClassName="pl-3">
            <span className="tw-details-field-title">
              <Icon
                name={
                  task.pickupTask
                    ? "arrowRightFromBracket"
                    : "arrowRightToBracket"
                }
              />
              {` ${task.destination?.address?.street || "No street address"}`}
            </span>
          </AccordionV2.Label>
          <AccordionV2.Content>
            <OnfleetTaskDetails task={task} />
          </AccordionV2.Content>
        </AccordionV2>
      ))}
    </div>
  );
}
